import React, { useState, useEffect } from 'react';
import { FileText, Eye } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { patientService } from '../services/patientService';
import BookingDetailsPopup from '../components/BookingDetailsPopup';
import CancelBookingPopup from '../components/CancelBookingPopup';
import EditBookingPopup from '../components/EditBookingPopup';

const PatientBookingsPage = () => {
  const navigate = useNavigate();

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient Admin';
  }, []);

  // State for real booking data
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Popup state
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  
  // Cancel popup state
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMessage, setCancelMessage] = useState('');

  // Edit popup state
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);

  // Fetch booking data on component mount
  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all bookings for the user
      const allBookings = await patientService.getUserBookings();
      console.log('All bookings fetched:', allBookings);
      
      // Get today's date for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Categorize bookings
      const upcomingBookings = [];
      const pastBookings = [];

      allBookings.forEach(booking => {
        const bookingDate = new Date(booking.appointment_date);
        bookingDate.setHours(0, 0, 0, 0);
        
        // Check if booking is upcoming (future date and scheduled status)
        if (bookingDate >= today && booking.status === 'Scheduled') {
          upcomingBookings.push(booking);
        } else {
          // All other bookings go to past bookings (including cancelled, completed, past dates)
          pastBookings.push(booking);
        }
      });

      // Sort upcoming bookings by date (earliest first)
      upcomingBookings.sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
      
      // Sort past bookings by date (most recent first)
      pastBookings.sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date));

      // Limit upcoming bookings to 1 for display (as per original logic)
      const finalUpcomingBookings = upcomingBookings.slice(0, 1);
      
      // Add remaining upcoming bookings to past bookings
      const remainingUpcoming = upcomingBookings.slice(1).map(booking => ({
        ...booking,
        status: 'Scheduled' // Mark as Scheduled for display in previous bookings
      }));
      
      const finalPastBookings = [...remainingUpcoming, ...pastBookings];

      console.log('Final upcoming bookings:', finalUpcomingBookings);
      console.log('Final past bookings:', finalPastBookings);
      
      // Debug: Log status values for past bookings
      finalPastBookings.forEach(booking => {
        console.log(`Booking ${booking.id}: status = "${booking.status}"`);
      });
      
      setUpcomingBookings(finalUpcomingBookings);
      setPastBookings(finalPastBookings);

    } catch (error) {
      console.error('Error fetching bookings:', error);
      setError(error.message || 'Failed to fetch bookings');
      
      // Handle 401 unauthorized error
      if (error.message.includes('Could not validate credentials') || error.message.includes('401')) {
        console.log('🔐 Authentication error detected. Please log in again.');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }
    } finally {
      setLoading(false);
    }
  };
  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes}${ampm}`;
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedBooking(null);
  };

  const handleEditBooking = (booking) => {
    setBookingToEdit(booking);
    setIsEditPopupOpen(true);
  };

  const handleCancelBooking = (booking) => {
    setBookingToCancel(booking);
    setIsCancelPopupOpen(true);
  };

  const handleConfirmCancel = async (bookingId) => {
    setCancelLoading(true);
    setCancelMessage('');
    
    try {
      await patientService.cancelPatientBooking(bookingId);
      setCancelMessage('Appointment cancelled successfully!');
      
      // Refresh bookings to reflect the changes
      await fetchBookings();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setCancelMessage('');
      }, 3000);
      
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setCancelMessage('Failed to cancel appointment. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setCancelMessage('');
      }, 5000);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCloseCancelPopup = () => {
    setIsCancelPopupOpen(false);
    setBookingToCancel(null);
  };

  const handleCloseEditPopup = () => {
    setIsEditPopupOpen(false);
    setBookingToEdit(null);
  };

  const handleEditSave = async () => {
    // Refresh bookings after edit
    await fetchBookings();
  };

  const handleGoToAppointment = (booking) => {
    console.log('📋 Patient viewing appointment details for booking:', booking.id);
    
    // Navigate to patient appointment display page
    navigate(`/patient/video-call/${booking.id}`, { 
      state: { 
        appointmentData: {
          booking_id: booking.id,
          patient_id: booking.patient_id,
          doctor_id: booking.doctor_id,
          appointment_type: booking.appointment_type,
          appointment_date: booking.appointment_date,
          appointment_time: booking.appointment_time,
          patient_name: booking.patient_name,
          doctor_name: booking.doctor,
          clinician_name: booking.clinician_name,
          reason: booking.reason,
          specialty: booking.specialty,
          clinic_location: booking.clinic_location,
          schedule_type: booking.schedule_type,
          status: booking.status
        }
      } 
    });
  };

  const handleDownloadReport = (bookingId) => {
    // In a real app, this would download the appointment report
    console.log('Downloading report for booking:', bookingId);
  };

  const renderBookingTable = (bookings, title, isUpcoming = false) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Loading bookings...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-red-600 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-600">{error}</p>
              <button 
                onClick={fetchBookings}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium">No {isUpcoming ? 'upcoming' : 'previous'} bookings found</p>
              <p className="text-gray-500 text-sm mt-1">
                {isUpcoming ? 'You have no scheduled appointments at the moment.' : 'You have no past appointments yet.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Date & Time:</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Appt. Type:</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Doctor:</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Facility:</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Address:</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 w-1/6">Status:</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div>{formatDate(booking.appointment_date)}</div>
                      <div className="text-gray-500">{formatTime(booking.appointment_time)}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {booking.appointment_type || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div>{booking.doctor || 'N/A'}</div>
                      <button
                        onClick={() => handleViewBooking(booking)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium transition-colors duration-200"
                      >
                        View
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {booking.clinic_location || 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div>{booking.clinic_location || 'N/A'}</div>
                      <div className="text-gray-500">Location</div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {isUpcoming ? (
                        <div className="flex flex-col items-center space-y-2">
                          {/* Status Badge */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'cancelled' || booking.status === 'Cancelled'
                              ? 'bg-red-500 text-white'
                              : booking.status === 'Scheduled'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status === 'completed' ? 'Completed' : 
                             booking.status === 'cancelled' || booking.status === 'Cancelled' ? 'Cancelled' : 
                             booking.status}
                          </span>
                          {/* Action Buttons */}
                          <button
                            onClick={() => handleEditBooking(booking)}
                            className="w-full bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-xs font-medium hover:bg-blue-200 transition-colors duration-200 flex items-center justify-center space-x-1"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleCancelBooking(booking)}
                            className="w-full bg-red-500 text-white px-3 py-1 rounded-md text-xs font-medium hover:bg-red-600 transition-colors duration-200 flex items-center justify-center space-x-1"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Cancel</span>
                          </button>
                          <button
                            onClick={() => handleGoToAppointment(booking)}
                            className="w-full bg-green-100 text-green-800 px-3 py-1 rounded-md text-xs font-medium hover:bg-green-200 transition-colors duration-200 flex items-center justify-center space-x-1"
                          >
                            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>View Appointment</span>
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center space-y-2">
                          {/* Status Badge */}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'cancelled' || booking.status === 'Cancelled'
                              ? 'bg-red-500 text-white'
                              : booking.status === 'Scheduled'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status === 'completed' ? 'Completed' : 
                             booking.status === 'cancelled' || booking.status === 'Cancelled' ? 'Cancelled' : 
                             booking.status}
                          </span>
                          
                          {/* Download Report Button - Only for completed bookings */}
                          {booking.status === 'completed' && (
                            <button
                              onClick={() => handleDownloadReport(booking.id)}
                              className="w-full bg-blue-100 text-blue-800 px-3 py-1 rounded-md text-xs font-medium hover:bg-blue-200 transition-colors duration-200 flex items-center justify-center space-x-1"
                              title="Download Report"
                            >
                              <FileText className="h-3 w-3" />
                              <span>Download Report</span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Success/Error Messages */}
      {cancelMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
          cancelMessage.includes('successfully') 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {cancelMessage.includes('successfully') ? (
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{cancelMessage}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-gray-100 min-h-screen p-4">
          <div className="space-y-2">
            <Link
              to="/patient/admin/mydetails"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              My Details
            </Link>
            <Link
              to="/patient/admin/preferences"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Preferences
            </Link>
            <Link
              to="/patient/admin/accounts"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Accounts
            </Link>
            <button
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-blue-600 text-white h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] shadow-md"
            >
              Bookings
            </button>
          </div>
        </div>

        {/* Right Content Area - 80% */}
        <div className="w-4/5 bg-gray-100 p-4">
          <div className="w-full">
            {/* Upcoming Bookings Section */}
            {renderBookingTable(upcomingBookings, 'Upcoming Bookings', true)}

                         {/* Previous Bookings Section */}
             {renderBookingTable(pastBookings, 'Previous Bookings', false)}
          </div>
        </div>
      </div>
      
      {/* Booking Details Popup */}
      <BookingDetailsPopup
        booking={selectedBooking}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
      />
      
      {/* Cancel Booking Popup */}
      <CancelBookingPopup
        booking={bookingToCancel}
        isOpen={isCancelPopupOpen}
        onClose={handleCloseCancelPopup}
        onConfirm={handleConfirmCancel}
      />
      
      {/* Edit Booking Popup */}
      <EditBookingPopup
        booking={bookingToEdit}
        isOpen={isEditPopupOpen}
        onClose={handleCloseEditPopup}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default PatientBookingsPage; 